import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EntryCard from "@/components/feature/EntryCard";
import { useSeo } from "@/hooks/useSeo";
import { categories, entries } from "@/mocks/entries";

export default function Entries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const filtered = useMemo(() => {
    return entries.filter((entry) => {
      const matchCat = activeCat === "all" || entry.category === activeCat;
      if (!matchCat) return false;
      if (!query.trim()) return true;
      const haystack = `${entry.title} ${entry.summary} ${entry.categoryName} ${entry.tags.join(" ")}`.toLowerCase();
      return haystack.includes(query.trim().toLowerCase());
    });
  }, [activeCat, query]);

  const updateParams = (next: { cat?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.cat !== undefined) {
      if (next.cat === "all") params.delete("cat");
      else params.set("cat", next.cat);
    }
    if (next.q !== undefined) {
      if (!next.q.trim()) params.delete("q");
      else params.set("q", next.q.trim());
    }
    setSearchParams(params, { replace: true });
  };

  const activeCategory = categories.find((c) => c.slug === activeCat);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "事件词条库 - 黑料百科",
        description: "按话题分类浏览黑料百科收录的全部事件词条，支持关键词搜索与分类筛选。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${window.location.origin}/` },
          { "@type": "ListItem", position: 2, name: "事件词条库", item: `${window.location.origin}/entries` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: filtered.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: entry.title,
          url: `${window.location.origin}/entry/${entry.slug}`,
        })),
      },
    ],
    [filtered]
  );

  useSeo({
    title: "事件词条库 - 黑料百科全部瓜条与事件背景记录",
    description:
      "黑料百科事件词条库，按娱乐明星、饭圈文化、直播电竞、网红达人、商业消费与演出综艺分类，支持关键词搜索，查看每个瓜条的事件背景与完整记录。",
    keywords: "事件词条,瓜条大全,事件背景,完整记录,黑料百科分类",
    path: "/entries",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-8 md:px-10 md:py-12">
          <Breadcrumbs items={[{ label: "首页", href: "/" }, { label: "事件词条库" }]} />
          <div className="mt-5 max-w-3xl">
            <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              事件词条库
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              全部词条按话题分类整理，每条都包含事件背景、时间线与完整记录。输入关键词，或点击分类快速定位。
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-5">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateParams({ q: keyword });
              }}
              className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row"
            >
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索词条标题、标签或关键词"
                  aria-label="搜索事件词条"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
              {(query || activeCat !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setSearchParams(new URLSearchParams(), { replace: true });
                  }}
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
                >
                  重置
                </button>
              )}
            </form>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ cat: "all" })}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-secondary-500 text-secondary-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300"
                }`}
              >
                全部话题
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => updateParams({ cat: cat.slug })}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === cat.slug
                      ? "bg-secondary-500 text-secondary-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-10 md:py-14">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {activeCategory ? activeCategory.name : "全部词条"}
              <span className="ml-2 text-sm font-normal text-foreground-500">
                共 {filtered.length} 条
              </span>
            </h2>
            {query && (
              <p className="text-sm text-foreground-500">
                关键词：<span className="font-medium text-foreground-800">{query}</span>
              </p>
            )}
          </div>

          {activeCategory && (
            <p className="mb-8 max-w-3xl rounded-lg border border-background-200 bg-background-100 p-4 text-sm leading-relaxed text-foreground-600">
              {activeCategory.intro}
            </p>
          )}

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((entry) => (
                <EntryCard key={entry.slug} entry={entry} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
                <i className="ri-search-eye-line text-2xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">没有找到匹配的词条</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                试试更换关键词，或切换到「全部话题」浏览。若你希望我们收录某个事件，可以提交补充线索。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部词条
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}